// Client-related TypeScript interfaces and types
import { PaginationInfo, BaseEntity, MeasurementSystem } from './common';
import {
    BMRFormulaId,
    Gender as CalculationGender,
    BMICategory,
    WeightGoalCode,
    PregnancyTrimester,
    LactationPeriod,
    LactationGoal,
} from './calculations';


export type ClientStatus = 'active' | 'inactive' | 'archived' | 'suspended' | 'draft' | 'pending_deletion';

export const CLIENT_ROSTER_STATUS_FILTERS = ['all', 'active', 'suspended', 'draft', 'pending_deletion'] as const;
export type ClientRosterStatusFilter = (typeof CLIENT_ROSTER_STATUS_FILTERS)[number];
export type ClientDashboardStatusFilter = ClientRosterStatusFilter | 'drafts';

export interface ClientAccountDeletion {
  status: 'pending_deletion';
  requestedAt: string;
  requestedBy: 'client';
  purgeAt: string;
  recoverableUntil: string;
  previousStatus: string;
}

export interface ClientsDashboardCounts {
  all: number;
  active: number;
  suspended: number;
  drafts: number;
  pendingDeletion: number;
}

export interface ClientDashboardItem {
  id: string;
  fullName: string;
  email: string;
  status: string;
  location?: string | null;
  phone?: string | null;
  phoneCountryCode?: string | null;
  questionnairesSentCount?: number;
  questionnaireProgress?: number;
  upcomingSessionDate?: string | null;
  lastSessionDate?: string | null;
  invoiceStatus?: string | null;
  accountDeletion?: ClientAccountDeletion | null;
  message?: string | null;
}

export interface ClientsDashboardResponse {
  counts: ClientsDashboardCounts;
  clients: ClientDashboardItem[];
  pagination: PaginationInfo;
}


/**
 * One question-answer pair stored with a medical condition (from questionnaire submission).
 */
export interface ClientMedicalConditionQuestionAnswer {
  questionId: string;
  questionText: string;
  field: string;
  value: unknown;
  unit?: string;
}

/**
 * Client medical condition - fields vary by condition type and questionnaire.
 * Not all conditions have all fields; structure depends on conditionId (thyroid, diabetes, pcos, etc.)
 * API returns medicalConditionDetails[] with questionAnswers and condition-specific nested objects.
 */
export interface ClientMedicalCondition {
  id: string;
  clientId: string;
  conditionId: string;
  conditionName?: string;
  conditionSubtype?: string;
  severity?: 'mild' | 'moderate' | 'severe';
  status?: 'active' | 'managed' | 'resolved';
  diagnosedDate?: string | null;
  notes?: string | null;
  addedAt?: string;
  addedBy?: string | null;
  updatedAt?: string;
  createdAt?: string;

  /** Raw question answers from the condition questionnaire (API shape). */
  questionAnswers?: ClientMedicalConditionQuestionAnswer[];

  /** API may return currentWeek/weekOfDiagnosis as string (e.g. "4", "12"). */
  pregnancyDetails?: { currentWeek?: number | string; prePregnancyBMI?: number; weightGain?: number };
  diagnosisDetails?: {
    weekOfDiagnosis?: number | string; duration?: string; ageAtDiagnosis?: number | string;
    initialMisdiagnosis?: string; identificationMethod?: string;
    diagnosisStatus?: string; cause?: string;
  };
  pregnancyHistory?: { previousGDM?: string | boolean };
  treatment?: {
    managementType?: string; medications?: string | string[];
    insulinRegimen?: string; insulinType?: string; rapidInsulinType?: string;
    basalInsulinType?: string; cgmType?: string; carbCounting?: string | boolean;
    insulinCarbRatio?: string; managementResponsibility?: string;
    treatmentStatus?: string;
  };
  monitoring?: {
    fastingGlucose?: number; postPrandial1h?: number; postPrandial2h?: number;
    fastingGlucoseUnit?: string; postPrandial1hUnit?: string; postPrandial2hUnit?: string;
    testingFrequency?: string; homeMonitoring?: string;
  };
  antibodies?: { gad?: string; tpo?: string };
  labStatus?: {
    cPeptide?: string; testosteroneChecked?: string | boolean;
    bloodTestsDone?: string | boolean;
  };
  safety?: {
    hypoFrequency?: string; severeHypos?: string;
    hypoglycemiaHistory?: string; diabulimia?: string;
  };
  carePlan?: { insulinDiscussion?: string };
  menstrualHistory?: {
    bloodSugarImpact?: string; menopausalStatus?: string;
    ageAtMenarche?: number; firstPeriod?: string;
    cycleImpact?: string;
  };
  menstrual?: { cycleRegularity?: string };
  fertility?: {
    pregnancyPlanning?: string; ttcStatus?: string;
    fertilityTreatment?: string;
  };
  comorbidities?: {
    autoimmune?: string | string[];
    diabetesComplications?: string | string[];
    pcosHistory?: string; metabolicIssues?: string | string[];
    pcos?: string | string[]; familyHistory?: { pcos?: string };
  };
  growth?: {
    heightPercentile?: string | number; weightPercentile?: string | number;
    bmiPercentile?: string | number; concerns?: string | string[];
  };
  lifestyle?: {
    schoolMeals?: string; schoolChallenges?: string | string[];
    eatingChallenges?: string | string[]; dietManagement?: string;
    screenTime?: string; physicalActivity?: string;
    familyInvolvement?: string; schoolImpact?: string;
  };
  familyHistory?: { type2Diabetes?: string; general?: string | string[]; pcos?: string };
  symptoms?: {
    reported?: string[]; utiFrequency?: string;
    physicalSigns?: string | string[]; appetiteChanges?: string;
    weightImpact?: string;
  };
  thyroidStatus?: { currentFunction?: string };
  diet?: { previousInterventions?: string | string[] };
  goals?: { weightConcern?: string; nutritionSupportGoals?: string | string[] };
  managementPriorities?: string[];
  previousDiets?: string[];
  carbCravings?: string;
  medication?: string;

  [key: string]: unknown;
}

// Common preferred contact method types
export type PreferredContactMethod = 'email' | 'phone' | 'sms';

export interface SharedClient extends BaseEntity {
  nutritionistId: string;
  // schemaVersion is inherited from BaseEntity as required

  // Personal Info
  email: string;
  phone: string | null;
  phoneCountryCode: string;
  fullName: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string; // ISO string
  gender: CalculationGender;
  sexAtBirth?: string | null; // Added from API response

  // Measurements
  heightCm: number;
  weightKg: number;
  targetWeightKg: number | null;
  waistCircumferenceCm: number | null;
  hipCircumferenceCm: number | null;
  bodyFatPercentage: number | null;

  // Activity & Lifestyle
  activityLevel: string;
  applyGrowthFactor: boolean;

  // Pregnancy & Lactation
  pregnancyTrimester: PregnancyTrimester;
  pregnancyStatus?: string; // API e.g. "Pregnant"
  prePregnancyWeightKg?: number | null;
  isTwinPregnancy: boolean;
  lactationPeriod: LactationPeriod;
  lactationGoal: LactationGoal | null;
  feedingType?: 'breastfed' | 'formula';

  // Formula Selection
  bmrFormulaId: BMRFormulaId;

  // Weight Goal
  weightGoal: WeightGoalCode | null;
  customCalorieAdjustment?: number | null;

  // Calculated Values
  bmi: number;
  bmiCategory: BMICategory;
  bmiCategoryDisplay: string;
  bmr: number | null;
  eer: number;
  eerOverride?: number; // Added
  maintenanceEER?: number | null;
  conditionAdjustedTargetCalories?: number | null;
  finalTargetCalories?: number | null;
  targetCalories?: number | null;
  eerWithoutGrowthFactor: number | null; // For children
  overrideMinimumEnergyIntake?: boolean; // Added
  palUsed: number | null;
  calculationDetails: {
    formulaUsed: string;
    formulaDisplayName: string;
    palUsed: number | null;
    weightGoalAdjustment: number;
    growthFactorApplied: boolean;
    minimumEnergyIntake?: number; // Added, replacing safetyFloorApplied
    countryCode?: string | null; // Added
    source: 'bmr_x_pal' | 'iom_direct' | 'pregnancy_table' | 'lactation_table';
    calculatedAt: string; // ISO string
  };

  // DRV (Dietary Reference Values) Requirements (API may return null)
  drvRequirements?: DRVRequirements | null;
  drvGuidelineId?: string | null;
  drvFormulaId?: string | null;
  drvSource?: string | null;

  // Medical & Notes
  medicalConditions: string | null;
  medications: string | null;
  notes: string | null;
  nutritionistNotes: string | null;
  allergies: string[];
  dietaryPreferences: string[];
  cuisineTypesPreferred?: string[];
  foodIntolerances?: string[];
  /** Condition-specific symptoms; API may return {} or nested objects. */
  symptoms?: Record<string, unknown>;
  biomarkers?: Biomarker[];
  medicalConditionDetails?: ClientMedicalCondition[];
  preConsultationMedicalConditions?: unknown[];
  healthGoals: string[];
  /** Free-text client health goal from nutritionist intake. */
  healthGoal?: string | null;
  /** API may return null. */
  clientGoals?: unknown | null;

  // Settings
  location: string;
  cityOrCounty?: string | null;
  preferredMeasurementSystem: MeasurementSystem;
  preferredContactMethod: PreferredContactMethod;

  // Status
  status: ClientStatus;
  source: string | null;
  accountDeletion?: ClientAccountDeletion | null;

  // Timestamps
  lastInteractionAt: string | null; // ISO string
}

export type ClientSubscriptionStatus = 'active' | 'inactive' | 'cancelled' | 'expired';

export interface ClientSubscriptionLineItem {
  description: string;
  planId?: string | null;
  consultationTypeId?: string | null;
}

export interface ClientActiveSubscription {
  id: string;
  clientId: string;
  nutritionistId: string;
  invoiceId: string;
  planIds: string[];
  lineItems: ClientSubscriptionLineItem[];
  startDate: string;
  endDate: string;
  status: ClientSubscriptionStatus;
  deactivatedAt: string | null;
  deactivationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClientResponse {
  client: SharedClient;
  activeSubscription?: ClientActiveSubscription | null;
}

// Client goal interface based on the API response structure
export interface ClientGoal {
  id: string;
  clientId: string;
  nutritionistId: string;
  eerGoalCalories: number;
  proteinGoalMin: number;
  proteinGoalMax: number;
  carbsGoalMin: number;
  carbsGoalMax: number;
  fatGoalMin: number;
  fatGoalMax: number;
  fiberGoalGrams: number;
  waterGoalLiters: number;
  allergies?: string[];
  preferences?: string[];
  cuisineTypes?: string[];
  goalStartDate: string;
  goalEndDate: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// Extend the shared Client type with frontend-specific fields
export interface Client extends SharedClient {
  // Legacy fields (for backward compatibility) or Frontend specific
  // Some of these might be redundant if SharedClient covers them, but kept for safety
  
  // Client specific requirements
  // clientNutritionRequirements?: ClientNutritionRequirement[];
  // clientMicronutrientRequirements?: ClientMicronutrientRequirement[];
  // clientGoal?: ClientGoal | null; // Replaced by clientGoals (plural)

  // Health metrics
  healthMetrics?: {
    bmi: number;
    bmiCategory: string;
    bmiLastCalculated?: string;
  };

  // Guidelines and notes
  guidelineNotes?: string | null;
  // guidelineUsed?: string | null; // Replaced by drvSource or drvRequirements.source
  macroGuidelineCountry?: string;

  // Additional properties (legacy)
  // client_nutrition_requirements?: any[];
  
  // Legacy fields that might have different names/types in shared but used in frontend
  // Shared has bmiCategory (string), frontend had bmiClassification (string)
  bmiClassification?: string;
  
  // Shared has eer (number), frontend had eerCalories (number)
  eerCalories?: number;
  
  // Shared has specific formulas, frontend had formulaId (string | number)
  // Shared: bmrFormulaId: BMRFormulaId
  // Frontend: formulaId?: string | number
  // formulaId?: string | number;
  
  // Shared: pregnancyTrimester (PregnancyTrimester), frontend: pregnancyStatus (PregnancyStatus)
  // pregnancyStatus?: PregnancyStatus;
  // lactationStatus?: LactationStatus;
  
  // Shared: weightGoal (WeightGoalCode | null), frontend: weightGoal (string | null) - compatible
  
  // Client goals from API (new structure)
  clientGoals?: {
    protein?: {
      // Direct structure (API may return this format)
      value?: number | null;
      unit?: string;
      // Nested structure (alternative format)
      grams?: {
        value: number | null;
        unit: string;
      };
      kcal?: {
        value: number | null;
        unit: string;
      };
    };
    carbohydrates?: {
      energyPercent?: {
        value: number | null;
        unit: string;
      };
      kcal?: {
        value: number | null;
        unit: string;
      };
      grams?: {
        value: number | null;
        unit: string;
      };
    };
    dietaryFibre?: {
      value: number | null;
      unit: string;
    };
    totalFat?: {
      // Direct structure (API may return this format)
      value?: number | null;
      unit?: string;
      // Nested structure (alternative format)
      grams?: {
        value: number | null;
        unit: string;
      };
      kcal?: {
        value: number | null;
        unit: string;
      };
    };
    saturatedFat?: {
      // Direct structure (API may return this format)
      value?: number | null;
      unit?: string;
      // Nested structure (alternative format)
      grams?: {
        value: number | null;
        unit: string;
      };
      kcal?: {
        value: number | null;
        unit: string;
      };
    };
    transFat?: {
      grams?: {
        value: number | null;
        unit: string;
      };
      kcal?: {
        value: number | null;
        unit: string;
      };
    };
    freeSugars?: {
      value: number | null;
      unit: string;
    };
    water?: {
      value: number | null;
      unit: string;
    };
  };
}

// Helper types
export interface MacroRange {
  max: number | null;
  min: number | null;
  note: string | null;
  unit: string | null;
}

// Interface for individual micronutrient with dynamic fields
export interface MicronutrientItem {
  unit: string;
  note?: string | null;
  [key: string]: any; // Allow any additional fields like rda, ul, ai, etc.
}

// Micronutrient details interface
export interface MicronutrientDetails {
  [key: string]: MicronutrientItem;
}

// Client nutrition requirement interface
export interface ClientNutritionRequirement {
  id: string;
  clientId: string;
  [key: string]: any; // Allow for dynamic properties
}

// Client micronutrient requirement interface
export interface ClientMicronutrientRequirement {
  id: string;
  clientId: string;
  [key: string]: any; // Allow for dynamic properties
}

// Common gender types - kept for other usages, though SharedClient uses its own Gender type
export type Gender = 'male' | 'female' | 'other';

// Common activity level types
export type ActivityLevel =
  | 'sedentary'
  | 'lightly_active'
  | 'moderately_active'
  | 'active'
  | 'very_active'
  | 'extra_active'
  | 'extremely_active'
  | 'athlete_boys'
  | 'athlete_girls';

// Common pregnancy status types (matches backend PregnancyTrimester)
export type PregnancyStatus = 'none' | 'first' | 'second' | 'third';

// Common lactation status types (matches backend LactationPeriod)
export type LactationStatus = 'none' | '0-6' | '6-12' | '12-18' | '18-24';

export interface Biomarker {
  id: string;
  clientId: string;
  biomarkerId: string;
  biomarkerName: string; // API may return empty string
  value: number;
  unit: string;
  status: string; // e.g. 'low' | 'normal' | 'borderline' | 'high' | 'critical'
  testedDate: string;
  addedAt: string;
  labName?: string | null;
  rangeSource?: 'report' | 'internal' | 'none' | null;
  rangeLowerBound?: number | null;
  rangeUpperBound?: number | null;
  referenceRangeText?: string | null;
  referenceGuideline?: string | null;
  interpretation?: string | null;
  questionnaireInstanceId?: string | null;
  trendDates?: string[];
}

// ============================================================================
// DRV (Dietary Reference Values) Requirements Types
// ============================================================================

/**
 * Nutrient requirement values from DRV guidelines
 */
export interface NutrientRequirement {
  nutrientId: string;
  displayName: string;
  unit: string;
  lrni: number | null;
  ear: number | null; 
  rda: number | null; 
  ai: number | null; 
  amdr: number | null; // Added
  amdrLow: number | null; 
  amdrHigh: number | null; 
  ul: number | null; 
  // Some API payloads also provide grams-based macro targets
  amdrGrams?: number | null; // Added
  amdrLowGrams?: number | null;
  amdrHighGrams?: number | null;
  ulGrams?: number | null;
  aiGrams?: number | null; // Added
  source: string; 
  fieldSources?: Record<string, string>; // Added
}

/**
 * Macronutrient requirements grouped by category
 */
export interface MacronutrientRequirements {
  carbohydrates: NutrientRequirement[];
  lipids: NutrientRequirement[];
  proteins: NutrientRequirement[];
}

/**
 * Micronutrient requirements grouped by category
 */
export interface MicronutrientRequirements {
  mineralsMajor: NutrientRequirement[];
  mineralsTrace: NutrientRequirement[];
  vitaminsFatSoluble: NutrientRequirement[];
  vitaminsWaterSoluble: NutrientRequirement[];
}

/**
 * Complete DRV Requirements structure from API
 */
export interface DRVRequirements {
  source: string; 
  fallbackSourcesUsed: string[]; 
  gender: 'male' | 'female';
  lifestageType: string; 
  lifestageUsed: string; 
  calculatedAt: string; 
  
  // Nutrient categories
  bioactive: NutrientRequirement[];
  macronutrients: MacronutrientRequirements;
  micronutrients: MicronutrientRequirements;
  other: NutrientRequirement[];
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Clients list response
 */
export interface ClientsResponse {
  clients: Client[];
  pagination: PaginationInfo;
}

/**
 * POST /clients body. Two modes:
 * - Quick: set intakeMode: "quick" (minimal onboarding / invite / payment).
 * - Flexible: omit intakeMode or set to anything other than "quick" (full profile; metrics optional).
 * - sendPaymentRequest is not used by client onboarding; invoices are created after an active client id is returned.
 */
export interface CreateClientPayload {
  intakeMode?: 'quick';
  /** When completing onboarding from a resumed draft, converts that draft in place instead of creating a new client. */
  draftClientId?: string;
  email: string;
  phone?: string;
  /** Required for flexible intake; optional for quick intake. */
  phoneCountryCode?: string;
  country?: string;
  cityOrCounty?: string | null;
  zipCode?: string | null;
  /** Required for flexible intake; optional for quick intake. */
  firstName?: string;
  /** Required for flexible intake; optional for quick intake. */
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  sendInviteToApp?: boolean;
  /** @default false per API */
  sendEmail?: boolean;
  /** Active core questionnaire `_id` values selected during client onboarding. */
  questionnaireIds?: string[];
  sendPaymentRequest?: boolean;
  inviteNotes?: string;
  sexAtBirth?: string;
  heightCm?: number;
  weightKg?: number;
  location?: string;
  preferredMeasurementSystem?: string;
  /** Ignored by API. */
  status?: ClientStatus;
  source?: string;
  notes?: string;
  nutritionistNotes?: string;
  medicalConditions?: string[];
  medicalConditionSubtypes?: Record<string, string>;
  allergies?: string[];
  intolerances?: string[];
  medications?: string;
  dietaryPreferences?: string[];
  cuisineTypesPreferred?: string[];
  healthGoals?: string[];
  healthGoal?: string | null;
  preferredContactMethod?: PreferredContactMethod;
  targetWeightKg?: number;
  guidelineUsed?: string;
  activityLevel?: ActivityLevel | string;
  pregnancyStatus?: PregnancyStatus;
  pregnancyTrimester?: PregnancyTrimester;
  prePregnancyWeightKg?: number | null;
  lactationStatus?: LactationStatus;
  lactationPeriod?: LactationPeriod;
  lactationGoal?: LactationGoal | string | null;
  weightGoal?: WeightGoalCode | string | null;
  customCalorieAdjustment?: number | null;
  formulaId?: string;
  bmrFormulaId?: string;
  drvFormulaId?: string;
  drvSource?: string;
  eerOverride?: number;
  waistCircumferenceCm?: number;
  hipCircumferenceCm?: number;
  bodyFatPercentage?: number;
  isTwinPregnancy?: boolean;
  overrideMinimumEnergyIntake?: boolean;
  feedingType?: 'breastfed' | 'formula';
  applyGrowthFactor?: boolean;
}

// ============================================================================
// Client Onboarding Drafts (Save as Draft)
// ============================================================================

/** Wizard progress/state saved with a draft — mirrors the Add Client onboarding wizard's own state shape. */
export interface ClientOnboardingDraftState {
  currentStep: number;
  isInviteOnlyMode?: boolean;
  inviteFlowSource?: 'step1_quick' | 'step2_full' | null;
  inviteMessage?: string;
  inviteActions?: {
    scheduleAppointment?: boolean;
    attachQuestionnaire?: boolean;
    requestPayment?: boolean;
  };
  selectedQuestionnaireIds?: string[];
  applyGrowthFactor?: boolean;
  formData: Record<string, unknown>;
  appointmentPayload?: unknown;
  pendingInvoiceDraft?: unknown;
  pendingInvoiceFormDraft?: unknown;
}

/** POST/PUT /clients/drafts payload. */
export interface SaveClientDraftPayload extends ClientOnboardingDraftState {
  draftClientId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  phoneCountryCode?: string;
  country?: string;
  location?: string;
  cityOrCounty?: string;
  zipCode?: string | null;
  dateOfBirth?: string;
  gender?: string;
}

/** Draft list item returned by GET /clients/drafts (and inside GET /clients/drafts/:id). */
export interface ClientDraftListItem {
  id: string;
  status: 'draft';
  questionnairesSentCount: number;
  questionnairesPending: number;
  questionnairesCompleted: number;
  fullName: string;
  email: string;
  location: string | null;
  lastSessionDate: string | null;
  upcomingSessionDate: string | null;
  invoiceStatus: string | null;
  questionnaireProgress: number | null;
}

export interface ClientDraftDetail extends ClientDraftListItem {
  onboardingDraft: ClientOnboardingDraftState | null;
}
